import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import PredictionCard from "components/card/PredictionCard";
import { IconButton } from "@mui/material";
import { MdNavigateBefore, MdOutlineNavigateNext } from "react-icons/md";

const PredictionsCarousel = ({ slides, maxSlidesCount = 2 }) => {
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(0);
  const [slidesPerPage, setSlidesPerPage] = useState(2);
  const [currentSlides, setCurrentSlides] = useState([]);
  const [direction, setDirection] = useState(0); // 1 for next, -1 for previous

  // Update slides per page based on screen size
  useEffect(() => {
    const updateSlidesPerPage = () => {
      const width = window.innerWidth;
      switch (true) {
        case width >= 1024:
          setSlidesPerPage(2); // Desktop - 2 cards side by side matching screenshot
          break;
        case width >= 640:
          setSlidesPerPage(1); // Tablet
          break;
        default:
          setSlidesPerPage(1); // Mobile
      }
    };

    updateSlidesPerPage();
    window.addEventListener("resize", updateSlidesPerPage);
    return () => window.removeEventListener("resize", updateSlidesPerPage);
  }, [maxSlidesCount]);

  const totalPages = Math.ceil((slides?.length || 0) / slidesPerPage);

  // Update current slides whenever the page or slides per page changes
  useEffect(() => {
    const start = currentPage * slidesPerPage;
    const end = start + slidesPerPage;
    setCurrentSlides((slides || []).slice(start, end));
  }, [currentPage, slidesPerPage, slides]);

  return (
    <div
      style={{ fontFamily: "Noto Sans Armenian" }}
      className="relative w-full bg-[#f8fafc] dark:bg-[#030014]/60 p-3.5 sm:p-6 md:p-8 rounded-2xl md:rounded-[32px] dark:border-white/5 overflow-hidden"
    >
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentPage}
          className="grid gap-4 sm:gap-6 justify-center w-full"
          style={{
            gridTemplateColumns: `repeat(${slidesPerPage}, minmax(0, 1fr))`,
          }}
          initial={(custom) => ({ opacity: 0, x: custom > 0 ? 30 : -30 })}
          animate={{ opacity: 1, x: 0 }}
          exit={(custom) => ({ opacity: 0, x: custom > 0 ? -30 : 30 })}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 30,
          }}
          custom={direction}
        >
          {currentSlides.map((prediction, index) => (
            <div key={index} className="flex-shrink-0 h-full">
              <PredictionCard prediction={prediction} />
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Bottom right navigation arrows matching screenshot */}
      <div className="flex justify-end mt-4 items-center gap-2">
        <button
          onClick={() => {
            if (currentPage > 0) {
              setDirection(-1);
              setCurrentPage(currentPage - 1);
            }
          }}
          disabled={currentPage === 0}
          className="w-10 h-10 rounded-full bg-[#e5ebff] hover:bg-[#0033FF] text-[#0033FF] hover:text-white disabled:opacity-40 disabled:hover:bg-[#e5ebff] disabled:hover:text-[#0033FF] flex items-center justify-center transition-all cursor-pointer"
        >
          <MdNavigateBefore size={26} />
        </button>

        {currentPage < totalPages - 1 ? (
          <button
            onClick={() => {
              if (currentPage < totalPages - 1) {
                setDirection(1);
                setCurrentPage(currentPage + 1);
              }
            }}
            disabled={currentPage >= totalPages - 1}
            className="w-10 h-10 rounded-full bg-[#e5ebff] hover:bg-[#0033FF] text-[#0033FF] hover:text-white disabled:opacity-40 disabled:hover:bg-[#e5ebff] disabled:hover:text-[#0033FF] flex items-center justify-center transition-all cursor-pointer"
          >
            <MdOutlineNavigateNext size={26} />
          </button>
        ) : (
          <button
            onClick={() => router.push("/predictions")}
            className="text-[#0033FF] font-bold text-sm hover:underline px-3 py-1 cursor-pointer"
          >
            Բոլորը
          </button>
        )}
      </div>
    </div>
  );
};

export default PredictionsCarousel;
